import React, { useState } from "react";
import { Loader2, ArrowRight, Pencil, Lock } from "lucide-react";
import { Button } from "@cms/ui/components/button";
import { Input } from "@cms/ui/components/input";
import { Label } from "@cms/ui/components/label";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@cms/ui/components/card";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@cms/ui/components/select";
import type { ApiClient } from "@cms/portal-lib/api";
import type { AdminClient } from "../lib/outlet-context";
import { PLAN_LABELS } from "../lib/plans";

const TENANT_ID_MAX = 32;

function toTenantId(name: string) {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, TENANT_ID_MAX);
}

export function ProvisionPanel({ clients, api, onClientCreated }: {
  clients: AdminClient[];
  api: ApiClient | null;
  onClientCreated: (client: AdminClient) => void;
}) {
  const [form, setForm] = useState({
    businessName: "",
    tenantId: "",
    plan: "launch",
  });
  const [tenantIdLocked, setTenantIdLocked] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const updateField = (field: "businessName" | "tenantId" | "plan") =>
    (e: string | React.ChangeEvent<HTMLInputElement>) => {
    const value = typeof e === "string" ? e : e.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const updateBusinessName = (e: React.ChangeEvent<HTMLInputElement>) => {
    const name = e.target.value;
    setForm((prev) => ({
      ...prev,
      businessName: name,
      tenantId: tenantIdLocked ? prev.tenantId : toTenantId(name),
    }));
  };

  const updateTenantId = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "").slice(0, TENANT_ID_MAX);
    setForm((prev) => ({ ...prev, tenantId: value }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError("");

    const tenantId = form.tenantId.trim().toLowerCase();
    if (clients.some((c) => c.id === tenantId)) {
      setError("That tenant ID already exists.");
      return;
    }

    if (!api) {
      setError("No API configured — set PUBLIC_API_BASE_URL and reload before creating clients.");
      return;
    }

    setSubmitting(true);
    try {
      const result = await api.request<{ client: AdminClient }>("/admin/clients", {
        method: "POST",
        body: JSON.stringify({
          tenant_id: tenantId,
          plan: form.plan,
          business_name: form.businessName.trim() || undefined,
        }),
      });
      setForm({ businessName: "", tenantId: "", plan: "launch" });
      setTenantIdLocked(false);
      onClientCreated(result.client);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Create client record</CardTitle>
        <CardDescription>
          Saves the client to the database. Provision infrastructure from the client overview page.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="businessName">Business name</Label>
            <Input
              id="businessName"
              value={form.businessName}
              onChange={updateBusinessName}
              placeholder="Barrow Cafe"
            />
          </div>
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label htmlFor="tenantId">Tenant ID</Label>
              <button
                type="button"
                onClick={() => setTenantIdLocked((l) => !l)}
                className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
              >
                {tenantIdLocked ? (
                  <><Lock className="h-3 w-3" /> Locked</>
                ) : (
                  <><Pencil className="h-3 w-3" /> Edit</>
                )}
              </button>
            </div>
            <Input
              id="tenantId"
              value={form.tenantId}
              onChange={updateTenantId}
              readOnly={!tenantIdLocked}
              required
              maxLength={TENANT_ID_MAX}
              placeholder="barrow-cafe"
              className={!tenantIdLocked ? "bg-muted text-muted-foreground cursor-default select-none" : ""}
            />
            <p className="text-xs text-muted-foreground text-right">{form.tenantId.length}/{TENANT_ID_MAX}</p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="plan">Plan</Label>
            <Select value={form.plan} onValueChange={updateField("plan")}>
              <SelectTrigger id="plan">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(PLAN_LABELS).map(([value, label]) => (
                  <SelectItem key={value} value={value}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-end">
            <Button type="submit" disabled={submitting} className="w-full">
              {submitting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <ArrowRight className="h-4 w-4" />
              )}
              {submitting ? "Creating…" : "Create client"}
            </Button>
          </div>
        </form>
        {error && <p className="mt-3 text-sm text-destructive">{error}</p>}
      </CardContent>
    </Card>
  );
}
